import {
  type BaseQueryFn,
  createApi,
  fetchBaseQuery,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'

import { clearAuth } from '../slices/authSlice'
import { getStoredToken, setStoredToken } from '@/services/auth'
import { API_BASE_URL } from '@/config/api'

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers) => {
    const token = getStoredToken()
    if (token) headers.set('authorization', `Bearer ${token}`)
    return headers
  },
})

// N2NAI §3.5 — auto-logout khi 401
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extra,
) => {
  const result = await rawBaseQuery(args, api, extra)
  if (result.error && result.error.status === 401) {
    setStoredToken(null)
    api.dispatch(clearAuth())
    if (window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
  } else if (result.error && result.error.status === 403) {
    // Fix 5 — BE `PermissionError` detail (vd "Role R013 needs EDIT on
    // purchase.local_p, has VIEW") lộ role_code + function key kỹ thuật,
    // người dùng không hiểu. Rewrite về string FE-friendly để page-level
    // `snack.error(err?.data?.detail)` hiện thông báo gọn. Giữ raw BE message
    // ở `__be_detail` cho dev debug qua console (không hiển thị cho user).
    const raw = (result.error.data as { detail?: string } | undefined)?.detail
    result.error = {
      ...result.error,
      data: {
        detail: "You don't have permission for this action.",
        __be_detail: raw,
      },
    }
    // Fix 9 — admin có thể vừa xóa quyền của user đang login. FE Redux.permissions
    // hiện stale → UI vẫn hiện nút enable nhưng API trả 403. Bắn event để Layout
    // re-fetch /auth/me + /settings/roles cập nhật permissions ngay.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tlc:perm-refresh'))
    }
  }
  return result
}

export const baseApi = createApi({
  reducerPath: 'tlcerpApi',
  baseQuery: baseQueryWithReauth,
  // Fresh-on-mount default: mọi query tự fetch tươi khi component mount lại /
  // đổi arg (chuyển tab, đóng-mở lại dialog) thay vì trả cache cũ — trước đây
  // chỉ F5 mới tươi. keepUnusedDataFor: 30 cho cache tự dọn sau 30s không dùng.
  // Endpoint cần override (vd keepUnusedDataFor: 0) vẫn thắng default này.
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 30,
  tagTypes: [
    'Auth',
    'User',
    'Role',
    'Vendor',
    'Bank',
    'LocalP',
    'LocalPDetail',
    'ContractAll',
    'Lookup',
    'LocalPPlan',
    'LocalPForward',
    'LocalPVer2',
    'PTBFFixation',
    'PTBFRolling',
    'NPEContract',
    'NPEFixation',
    'NPEAlloc',
    'PayData',
    'PayDataDetail',
    'ProcInvoice',
    'ProcInvoiceDetail',
    'ProcGoodAlc',
    'ProcGoodAlcDetail',
    'ProcCvbb',
    'ProcCvbbDetail',
    'ProcReference',
    'Factory',
    'FactoryBom',
    'FactoryCost',
  ],
  endpoints: () => ({}),
})
